import { createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import Cookies from "js-cookie";
import { queueSystemLog } from "../../services/systemLogs";
import { safeParseCookie } from "../../utils/safeParseCookie";

// Cookies are parsed defensively: a corrupt value (older build, the literal
// "undefined", truncated JSON) falls back instead of throwing at module load
// and white-screening the whole app.
const storedUser = safeParseCookie("user");
const storedTheme = safeParseCookie("theme");

const initialValue = {
  userinfo: storedUser && typeof storedUser === "object" ? storedUser : null,
  theme:
    typeof storedTheme === "string" && storedTheme
      ? storedTheme
      : window.matchMedia &&
        window.matchMedia("(prefers-color-scheme: dark)").matches
      ? "dark"
      : "light",
  fcm: safeParseCookie("fcm"),
};
export const userSlice = createSlice({
  name: "user",
  initialState: initialValue,

  reducers: {
    login: (state, action) => {
      state.userinfo = action.payload.data.user;
      Cookies.set("user", JSON.stringify(action.payload.data.user), {
        expires: 90,
      });
      Cookies.set("token", JSON.stringify(action.payload.data.token), {
        expires: 90,
      });
      axios.defaults.headers.common[
        "Authorization"
      ] = `Bearer ${action.payload.data.token}`;
      queueSystemLog({
        level: "info",
        category: "auth",
        event: "session.login",
        message: "Web session login completed",
        actor: {
          userId: action.payload.data.user?._id || "",
          username: action.payload.data.user?.username || "",
          displayName: [
            action.payload.data.user?.first_name,
            action.payload.data.user?.last_name,
          ]
              .filter(Boolean)
              .join(" "),
        },
      });
    },

    updateProfilePhoto: (state, action) => {
      state.userinfo.photo = action.payload;
      Cookies.set("user", JSON.stringify(state.userinfo), {
        expires: 90,
      });
    },

    updateCoverPhoto: (state, action) => {
      state.userinfo.cover = action.payload;
      Cookies.set("user", JSON.stringify(state.userinfo), {
        expires: 90,
      });
    },

    updateUserInfo: (state, action) => {
      state.userinfo = {
        ...(state.userinfo || {}),
        ...(action.payload || {}),
      };
      Cookies.set("user", JSON.stringify(state.userinfo), {
        expires: 90,
      });
    },

    updateNStats: (state, action) => {
      state.userinfo.recivedRequestsCount =
        action.payload.recivedRequestsCount >= 0
          ? action.payload.recivedRequestsCount
          : state.userinfo.recivedRequestsCount;

      state.userinfo.unseenMessages =
        action.payload.unseenMessages >= 0
          ? action.payload.unseenMessages
          : state.userinfo.unseenMessages;

      state.userinfo.unseenNotification =
        action.payload.unseenNotification >= 0
          ? action.payload.unseenNotification
          : state.userinfo.unseenNotification;

      Cookies.set("user", JSON.stringify(state.userinfo), {
        expires: 90,
      });
    },

    decrementUnseen: (state, action) => {
      const type = action.payload?.type;
      if (type === "notification" && state.userinfo.unseenNotification > 0) {
        state.userinfo.unseenNotification -= 1;
      } else if (type === "message" && state.userinfo.unseenMessages > 0) {
        state.userinfo.unseenMessages -= 1;
      }
      Cookies.set("user", JSON.stringify(state.userinfo), { expires: 90 });
    },

    reciveNoti: (state, action) => {
      if (action.payload?.type === "notification") {
        state.userinfo.unseenNotification =
          state.userinfo.unseenNotification + 1;
      } else if (action?.payload.type === "message") {
        state.userinfo.unseenMessages = state.userinfo.unseenMessages + 1;
      }

      Cookies.set("user", JSON.stringify(state.userinfo), {
        expires: 90,
      });
    },

    logout: (state, action) => {
      state.userinfo = null;
      Cookies.set("user", null);
      Cookies.set("token", null);
      queueSystemLog({
        level: "info",
        category: "auth",
        event: "session.logout",
        message: "Web session logout completed",
      });
    },

    changeTheme: (state, action) => {
      state.theme = action.payload;
      Cookies.set("theme", JSON.stringify(action.payload));
    },
  },
});
export const {
  login,
  logout,
  updateProfilePhoto,
  updateCoverPhoto,
  updateUserInfo,
  changeTheme,
  updateNStats,
  reciveNoti,
  decrementUnseen,
} = userSlice.actions;

export default userSlice.reducer;
